import { Dispatch, SetStateAction, createContext, useContext, useMemo, useState } from 'react'
import { QueryStatus, useQuery } from 'react-query'
import { Outlet } from 'react-router-dom'

import _ from 'lodash'

import {
  loadOrdersInTransit,
  loadOrdersToDropOff,
  loadOrdersToPickup,
  loadOrdersToSeparate,
} from '@services/hubii/hub-service'

import {
  LoadOrdersInTransit,
  LoadOrdersToDropOff,
  LoadOrdersToPickUp,
  LoadOrdersToSeparate,
} from '@services/hubii/hub-service/types'

import { useAuthStore } from '@stores/auth.context/auth.context'

type TOrderListLayoutContext = {
  search: string
  setSearch: Dispatch<SetStateAction<string>>
  ordersToDropOff?: LoadOrdersToDropOff.Response
  ordersToDropOffStatus: QueryStatus
  ordersToDropOffParams: LoadOrdersToDropOff.Request
  setOrdersToDropOffParams: Dispatch<SetStateAction<LoadOrdersToDropOff.Request>>
  refetchOrdersToDropOff: () => void
  ordersToSeparate?: LoadOrdersToSeparate.Response
  ordersToSeparateStatus: QueryStatus
  ordersToSeparateParams: LoadOrdersToSeparate.Request
  setOrdersToSeparateParams: Dispatch<SetStateAction<LoadOrdersToSeparate.Request>>
  refetchOrdersToSeparate: () => void
  ordersToPickup?: LoadOrdersToPickUp.Response
  ordersToPickupStatus: QueryStatus
  ordersToPickupParams: LoadOrdersToPickUp.Request
  setOrdersToPickupParams: Dispatch<SetStateAction<LoadOrdersToPickUp.Request>>
  refetchOrdersToPickup: () => void
  ordersInTransit?: LoadOrdersInTransit.Response
  ordersInTransitStatus: QueryStatus
  ordersInTransitParams: LoadOrdersInTransit.Request
  setOrdersInTransitParams: Dispatch<SetStateAction<LoadOrdersInTransit.Request>>
  refetchOrdersInTransit: () => void
  refetchAll: () => void
}

const OrderListLayoutContext = createContext<TOrderListLayoutContext>({
  search: '',
  setSearch: () => {},
  refetchAll: () => {},

  //to-dropoff
  ordersToDropOff: undefined,
  ordersToDropOffStatus: 'idle',
  ordersToDropOffParams: {
    search: '',
    paginate: true,
    page: 1,
  },
  setOrdersToDropOffParams: () => {},
  refetchOrdersToDropOff: () => {},

  //to-separate
  ordersToSeparate: undefined,
  ordersToSeparateStatus: 'idle',
  ordersToSeparateParams: {
    search: '',
    paginate: true,
    page: 1,
  },
  setOrdersToSeparateParams: () => {},
  refetchOrdersToSeparate: () => {},

  //to-pickup
  ordersToPickup: undefined,
  ordersToPickupStatus: 'idle',
  ordersToPickupParams: {
    search: '',
    paginate: true,
    page: 1,
  },
  setOrdersToPickupParams: () => {},
  refetchOrdersToPickup: () => {},

  //in-transit
  ordersInTransit: undefined,
  ordersInTransitStatus: 'idle',
  ordersInTransitParams: {
    search: '',
    paginate: true,
    page: 1,
  },
  setOrdersInTransitParams: () => {},
  refetchOrdersInTransit: () => {},
})

export const useOrderListLayoutProvider = () => useContext(OrderListLayoutContext)

const OrdersListLayoutProvider = () => {
  const { selectedHub } = useAuthStore()

  const [search, setSearch] = useState('')

  const [ordersToDropOffParams, setOrdersToDropOffParams] = useState<LoadOrdersToDropOff.Request>({
    search,
    paginate: true,
    page: 1,
  })

  const {
    data: ordersToDropOff,
    status: ordersToDropOffStatus,
    refetch: refetchOrdersToDropOff,
  } = useQuery({
    queryKey: ['orders', 'to_dropOff', selectedHub!.uuid, ordersToDropOffParams],
    queryFn: () => loadOrdersToDropOff(selectedHub!.uuid, ordersToDropOffParams),
  })

  const [ordersToSeparateParams, setOrdersToSeparateParams] =
    useState<LoadOrdersToSeparate.Request>({
      search,
      paginate: true,
      page: 1,
    })

  const {
    data: ordersToSeparate,
    status: ordersToSeparateStatus,
    refetch: refetchOrdersToSeparate,
  } = useQuery({
    queryKey: ['orders', 'to_separate', selectedHub!.uuid, ordersToSeparateParams],
    queryFn: () => loadOrdersToSeparate(selectedHub!.uuid, ordersToSeparateParams),
  })

  const [ordersToPickupParams, setOrdersToPickupParams] = useState<LoadOrdersToPickUp.Request>({
    search,
    paginate: true,
    page: 1,
  })

  const {
    data: ordersToPickup,
    status: ordersToPickupStatus,
    refetch: refetchOrdersToPickup,
  } = useQuery({
    queryKey: ['orders', 'to_pickup', selectedHub!.uuid, ordersToPickupParams],
    queryFn: () => loadOrdersToPickup(selectedHub!.uuid, ordersToPickupParams),
  })

  const [ordersInTransitParams, setOrdersInTransitParams] = useState<LoadOrdersInTransit.Request>({
    search,
    paginate: true,
    page: 1,
  })

  const {
    data: ordersInTransit,
    status: ordersInTransitStatus,
    refetch: refetchOrdersInTransit,
  } = useQuery({
    queryKey: ['orders', 'in_transit', selectedHub!.uuid, ordersInTransitParams],
    queryFn: () => loadOrdersInTransit(selectedHub!.uuid, ordersInTransitParams),
  })

  const refetchAll = () => {
    refetchOrdersToSeparate()
    refetchOrdersToDropOff()
    refetchOrdersToPickup()
    refetchOrdersInTransit()
  }

  const contextValue = useMemo(
    () => ({
      search,
      setSearch,
      ordersToDropOff,
      ordersToDropOffStatus,
      ordersToDropOffParams,
      setOrdersToDropOffParams,
      refetchOrdersToDropOff,
      ordersToSeparate,
      ordersToSeparateStatus,
      ordersToSeparateParams,
      setOrdersToSeparateParams,
      refetchOrdersToSeparate,
      ordersToPickup,
      ordersToPickupStatus,
      ordersToPickupParams,
      setOrdersToPickupParams,
      refetchOrdersToPickup,
      ordersInTransit,
      ordersInTransitStatus,
      ordersInTransitParams,
      setOrdersInTransitParams,
      refetchOrdersInTransit,
      refetchAll,
    }),
    [
      ordersToDropOffStatus,
      ordersToDropOffParams,
      setOrdersToDropOffParams,
      ordersToPickup,
      ordersToPickupStatus,
      ordersToPickupParams,
      setOrdersToPickupParams,
      ordersToSeparate,
      ordersToSeparateStatus,
      ordersToSeparateParams,
      setOrdersToSeparateParams,
      refetchOrdersToSeparate,
      ordersToDropOff,
      ordersInTransit,
      ordersInTransitStatus,
      ordersInTransitParams,
      setOrdersInTransitParams,
      search,
    ],
  )

  return (
    <OrderListLayoutContext.Provider value={contextValue}>
      <Outlet />
    </OrderListLayoutContext.Provider>
  )
}

export default OrdersListLayoutProvider
